import { useMemo } from 'react';

import { useIsAccountOnboarding, useIsAccountOnboardingObservable } from './account';
import { useTargetingParameters, useTargetingParametersObservable } from './targeting';
import { useIsDesktop } from '../styles/useThemeHooks';
import type { ProviderOnboardingParameters } from '../api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';

export const isWithMenuEditorControlValue = false;
export const isSelfServeMenuEnabledControlValue = false;
export const isOcrDraftEditingEnabledControlValue = false;

export const SSU_EXPERIMENT_EVENTS = {
  LOADED: 'menu_editor_experiment:assigned',
  GOTO_MENU_EDITOR: 'menu_editor_experiment:cta_clicked',
  RETURN_TO_ONBOARDING: 'menu_editor_experiment:complete_signup_clicked',
  MENU_EDITOR_PUBLISHED: 'menu_editor_experiment:menu_published',
};

export interface MenuEditorParams {
  isAccountOnboarding: boolean;
  isWithMenuEditor: boolean;
  isDesktop: boolean;
}

export function shouldEnableMenuEditor(params: MenuEditorParams): boolean {
  return params.isAccountOnboarding && params.isDesktop && params.isWithMenuEditor;
}

export function useProviderOnboardingParameters(): NonNullable<ProviderOnboardingParameters> {
  return useTargetingParameters().provider_onboarding ?? {};
}

export function useWithMenuEditor(): boolean {
  return useProviderOnboardingParameters().withMenuEditor ?? isWithMenuEditorControlValue;
}

export function useMenuEditorExperimentEnabled(): boolean {
  return useProviderOnboardingParameters().withMenuEditor !== undefined;
}

export function useWithMenuEditorObservable(): boolean {
  return useTargetingParametersObservable(
    'provider_onboarding',
    'withMenuEditor',
    isWithMenuEditorControlValue,
  );
}

export function useIsSelfServeMenuEnabledObservable(): boolean {
  return useTargetingParametersObservable(
    'provider_onboarding',
    'enableSelfServeMenuCreation',
    isSelfServeMenuEnabledControlValue,
  );
}

export function useIsOcrDraftEditingEnabledObservable(): boolean {
  return useTargetingParametersObservable(
    'provider_onboarding',
    'enableOcrDraftEditing',
    isOcrDraftEditingEnabledControlValue,
  );
}

export function useShouldEnableMenuEditorParams(): MenuEditorParams {
  const isAccountOnboarding = useIsAccountOnboarding();
  const isWithMenuEditor = useWithMenuEditor();
  const isDesktop = useIsDesktop();

  return useMemo(() => {
    return {
      isAccountOnboarding,
      isWithMenuEditor,
      isDesktop,
    };
  }, [isAccountOnboarding, isDesktop, isWithMenuEditor]);
}

export function useShouldEnableMenuEditorObservableParams(): MenuEditorParams {
  const isAccountOnboarding = useIsAccountOnboardingObservable();
  const isWithMenuEditor = useWithMenuEditorObservable();
  const isDesktop = useIsDesktop();

  return useMemo(() => {
    return {
      isAccountOnboarding,
      isWithMenuEditor,
      isDesktop,
    };
  }, [isAccountOnboarding, isDesktop, isWithMenuEditor]);
}

export function useShouldEnableMenuEditor(): boolean {
  return shouldEnableMenuEditor(useShouldEnableMenuEditorParams());
}

export function useShouldEnableMenuEditorObservable(): boolean {
  return shouldEnableMenuEditor(useShouldEnableMenuEditorObservableParams());
}
